import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Collapse from '@mui/material/Collapse';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import NavItem from './NavItem';
import Dot from 'components/@extended/Dot';
import IconButton from 'components/@extended/IconButton';
import SimpleBar from 'components/third-party/SimpleBar';
import Transitions from 'components/@extended/Transitions';
import { MenuOrientation } from 'config';

import useConfig from 'hooks/useConfig';
import useHeaderTheme from 'hooks/useHeaderTheme';
import useMenuCollapse from 'hooks/useMenuCollapse';
import { useGetMenuMaster } from 'api/menu';

import { useLanguage } from '../../../../../utils/i18n';
// assets
import BorderOutlined from '@ant-design/icons/BorderOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';

// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 200,
  '& > .MuiBox-root': {
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 25,
      ...(theme.direction !== 'rtl' && { left: -5 }),
      ...(theme.direction === 'rtl' && { right: -5 }),
      width: 10,
      height: 10,
      background: theme.palette.background.paper,
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 120,
      borderLeft: '2px solid',
      borderLeftColor: theme.palette.divider,
      borderBottom: '2px solid',
      borderBottomColor: theme.palette.divider
    }
  },
  '&[data-popper-placement="right-end"]': {
    '.MuiPaper-root': {
      marginBottom: -8
    },
    '&:before': {
      top: 'auto',
      bottom: 5
    }
  }
}));

// --- helpers ---
export const getParentPathForHiddenRoute = (path) => {
  const p = String(path || '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  if (p === 'history-download-video' || p === 'history-video') return 'live-streaming';
  if (p === 'dashboard' || p === 'dashboard') return 'dashboard-vehicle';
  return p;
};

function stripQueryAndSlashes(u = '') {
  if (!u) return '';
  const withoutQuery = String(u).split('?')[0];
  return withoutQuery.replace(/^\/+|\/+$/g, '');
}

function matchesPath(targetPath = '', candidate = '') {
  const t = getParentPathForHiddenRoute(targetPath);
  const c = String(candidate || '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();

  if (!c) return false;
  if (t === c) return true;
  if (t.startsWith(c + '/')) return true;
  if (c.includes(':')) {
    const pattern = '^' + c.replace(/:[^/]+/g, '[^/]+') + '$';
    try {
      return new RegExp(pattern, 'i').test(t);
    } catch (e) {
      console.log(e);
      return false;
    }
  }
  if (t.endsWith('/' + c) || c.endsWith('/' + t)) return true;
  const tLast = t.split('/').filter(Boolean).pop() || '';
  const cLast = c.split('/').filter(Boolean).pop() || '';
  if (tLast && cLast && tLast === cLast) return true;

  return false;
}

function findMatchRecursively(nodes = [], targetPath = '', nameFromQuery = '') {
  if (!Array.isArray(nodes)) return null;
  for (const node of nodes) {
    const rawUrl = node?.url || node?.link || '';
    const nodeUrl = stripQueryAndSlashes(rawUrl);

    const nodeQs = new URLSearchParams(rawUrl.split('?')[1] || '');
    const nodeNameFromQuery = (nodeQs.get('name') || node?.modules_name || '').toLowerCase();

    if (nodeUrl) {
      const isPathMatched = matchesPath(targetPath, nodeUrl);

      if (isPathMatched) {
        if (nameFromQuery && nodeNameFromQuery) {
          if (nameFromQuery === nodeNameFromQuery) {
            return { node, path: nodeUrl };
          }
        } else if (nameFromQuery && !nodeNameFromQuery) {
          // do not match
        } else if (!nameFromQuery && nodeNameFromQuery) {
          // do not match
        } else {
          return { node, path: nodeUrl };
        }
      }
    }

    if (Array.isArray(node.children) && node.children.length) {
      const found = findMatchRecursively(node.children, targetPath, nameFromQuery);
      if (found) return found;
    }
  }
  return null;
}

// ==============================|| NAV COLLAPSE ||============================== //

export default function NavCollapse({ menu, level = 1, parentId, setSelectedItems, selectedItems, setSelectedLevel, selectedLevel }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();
  const { headerColor, isLightHeader, iconColor } = useHeaderTheme();
  const navigation = useNavigate();
  const { t } = useLanguage();
  const translatedTitle = menu.module_name
    ? t(`menu.${menu.module_name}`, menu.alias || menu.title || '')
    : t(`menu.${menu.id}`, menu.title || '');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const [anchorElCollapse, setAnchorElCollapse] = useState(null);

  const openCollapse = Boolean(anchorElCollapse);
  const handleClickCollapse = (event) => {
    setAnchorElCollapse(event.currentTarget);
  };
  const handleCloseCollapse = () => {
    setAnchorElCollapse(null);
  };

  const handleClick = (event, isRedirect) => {
    setAnchorEl(null);
    setSelectedLevel(level);
    if (drawerOpen) {
      setOpen((prev) => !prev);
      setSelected((prev) => (!prev ? menu.id : null));
      setSelectedItems((prev) => (!selected ? menu.id : ''));
      if (menu.url && isRedirect) navigation(`${menu.url}`);
    } else {
      setAnchorEl(event?.currentTarget);
    }
  };

  const handlerIconLink = () => {
    if (!drawerOpen) {
      if (menu.url) navigation(`${menu.url}`);
      setSelected(menu.id);
    }
  };

  const handleHover = (event) => {
    setAnchorEl(event?.currentTarget);
  };

  const miniMenuOpened = Boolean(anchorEl);

  const handleMiniClose = () => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    setOpen(false);
    if (!miniMenuOpened) {
      if (!menu.url) {
        setSelected(null);
      }
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    if (selected === selectedItems) {
      if (level === 1) {
        setOpen(true);
      }
    } else {
      if (level === selectedLevel) {
        setOpen(false);
        if (!miniMenuOpened && !drawerOpen && !selected) {
          setSelected(null);
        }
        if (drawerOpen) {
          setSelected(null);
        }
      }
    }
  }, [selectedItems, level, selected, miniMenuOpened, drawerOpen, selectedLevel]);

  const { pathname, search } = useLocation();
  const targetPath = getParentPathForHiddenRoute(pathname);
  const qs = new URLSearchParams(search);
  const rawNameFromQuery = (qs.get('name') || '').toLowerCase();
  const nameFromQuery = getParentPathForHiddenRoute(rawNameFromQuery);

  useMenuCollapse(menu, pathname, miniMenuOpened, setSelected, setOpen, setAnchorEl);

  const rawUrl = menu?.url || menu?.link || '';
  const collapseUrl = stripQueryAndSlashes(rawUrl);
  let isPathMatched = false;

  if (collapseUrl && matchesPath(targetPath, collapseUrl)) {
    isPathMatched = true;
  } else {
    const rootNodes = Array.isArray(menu?.children) ? menu.children : [];
    const found = findMatchRecursively(rootNodes, targetPath, nameFromQuery);
    if (found) {
      isPathMatched = true;
    }
  }

  useEffect(() => {
    if (!menu) return;

    if (isPathMatched) {
      setSelected(menu.id);
      setOpen(true);
      setSelectedItems(menu.id);
      setSelectedLevel(level);
    } else {
      if (!miniMenuOpened) {
        setOpen(false);
        setSelected(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPath, nameFromQuery, menu, miniMenuOpened, drawerOpen]);

  const navCollapse = menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return (
          <NavCollapse
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            menu={item}
            level={level + 1}
            parentId={parentId}
          />
        );
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Collapse or Item
          </Typography>
        );
    }
  });

  const isSelected = selected === menu.id || isPathMatched;
  const borderIcon = level === 1 ? <BorderOutlined style={{ fontSize: '1rem' }} /> : false;
  const Icon = menu.icon;
  const menuIcon = menu.icon ? <Icon style={{ fontSize: drawerOpen ? '1.25rem' : '1.5rem' }} /> : borderIcon;

  const popperId = miniMenuOpened ? `collapse-pop-${menu.id}` : undefined;
  const FlexBox = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' };

  const collapsedIconStyle = { fontSize: '0.625rem', color: 'inherit' };
  const collapsedIcon = miniMenuOpened || open ? <UpOutlined style={collapsedIconStyle} /> : <DownOutlined style={collapsedIconStyle} />;

  // === WARNA TEMA KHUSUS ===
  const isWhite =
    headerColor?.toLowerCase() === '#ffffff' || headerColor?.toLowerCase() === 'white' || headerColor?.toLowerCase() === '#fff';
  const activeBg = isWhite ? '#485D9A' : headerColor || '#485D9A';
  const showDarkEffects = isWhite || !isLightHeader;

  const activeGradient = showDarkEffects ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0) 100%)' : 'none';
  const hoverGradient = showDarkEffects ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(255, 255, 255, 0) 100%)' : 'none';
  const activeShadow = showDarkEffects ? '0 4px 10px rgba(0, 0, 0, 0.15)' : '0 4px 10px rgba(0, 0, 0, 0.05)';
  const activeColor = isWhite ? '#ffffff' : iconColor;
  const inactiveHoverBg = isLightHeader ? 'rgba(0, 0, 0, 0.05)' : 'rgba(72, 93, 154, 0.08)';

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <>
          <ListItemButton
            id={`${menu.id}-button`}
            selected={isSelected}
            {...(!drawerOpen && { onMouseEnter: (e) => handleClick(e, true), onMouseLeave: handleMiniClose })}
            onClick={(e) => handleClick(e, true)}
            sx={(theme) => ({
              mb: 0.5,
              pl: drawerOpen ? `${level * 28}px` : 1.5,
              py: !drawerOpen && level === 1 ? 1.25 : 1,
              borderRadius: theme.shape?.borderRadius ? theme.shape.borderRadius * 4 : 12,

              // === STATE INACTIVE ===
              color: 'text.primary',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: inactiveHoverBg,
                backgroundImage: hoverGradient
              },

              // === STATE ACTIVE ===
              '&.Mui-selected': {
                backgroundColor: `${activeBg} !important`,
                backgroundImage: `${activeGradient} !important`,
                boxShadow: activeShadow,
                color: `${activeColor} !important`,
                '& .MuiListItemIcon-root': {
                  color: `${activeColor} !important`
                },
                '&:hover': {
                  backgroundColor: `${activeBg} !important`,
                  backgroundImage: `${activeGradient} !important`
                }
              },

              // === KHUSUS MINI DRAWER (Tutup) ===
              ...(!drawerOpen &&
                level === 1 && {
                  justifyContent: 'center',
                  px: 0,
                  width: 48,
                  height: 48,
                  margin: '0 auto 8px auto'
                }),

              // === KHUSUS ANAK MENU DI DALAM POPUP (Level > 1) ===
              ...(!drawerOpen &&
                level !== 1 && {
                  width: '100%',
                  justifyContent: 'flex-start',
                  px: 2
                })
            })}
            {...(drawerOpen &&
              menu.isDropdown && {
                'aria-controls': openCollapse ? `${menu.id}-menu` : undefined,
                'aria-haspopup': true,
                'aria-expanded': openCollapse ? 'true' : undefined,
                onClick: handleClickCollapse
              })}
          >
            {menuIcon && (
              <ListItemIcon
                onClick={handlerIconLink}
                sx={{
                  minWidth: 28,
                  color: isSelected ? activeColor : 'text.secondary',
                  ...(!drawerOpen && {
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 'auto'
                  })
                }}
              >
                {menuIcon}
              </ListItemIcon>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'inherit',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {translatedTitle}
                  </Typography>
                }
                secondary={
                  menu.caption && (
                    <Typography variant="caption" sx={{ color: isSelected ? 'inherit' : 'text.secondary', opacity: isSelected ? 0.8 : 1 }}>
                      {t(menu.caption)}
                    </Typography>
                  )
                }
              />
            )}

            {(drawerOpen || (!drawerOpen && level !== 1)) &&
              (menu?.url ? (
                <IconButton
                  onClick={(event) => {
                    event?.stopPropagation();
                    handleClick(event, false);
                  }}
                  size="small"
                  sx={{
                    width: 20,
                    height: 20,
                    mr: '-5px',
                    color: isSelected ? activeColor : 'text.secondary',
                    '&:hover': { backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : inactiveHoverBg },
                    ...((miniMenuOpened || open) && { ...(miniMenuOpened && { transform: 'rotate(90deg)' }) })
                  }}
                >
                  {collapsedIcon}
                </IconButton>
              ) : (
                <Box
                  component="span"
                  sx={{
                    color: 'inherit',
                    ...((miniMenuOpened || open) && { ...(miniMenuOpened && { transform: 'rotate(90deg)' }) })
                  }}
                >
                  {collapsedIcon}
                </Box>
              ))}

            {!drawerOpen && (
              <PopperStyled open={miniMenuOpened} anchorEl={anchorEl} placement="right-start" style={{ zIndex: 2001 }}>
                {({ TransitionProps }) => (
                  <Transitions in={miniMenuOpened} {...TransitionProps}>
                    <Paper
                      sx={(theme) => ({
                        overflow: 'hidden',
                        boxShadow: theme.customShadows.z1,
                        backgroundImage: 'none',
                        border: '1px solid',
                        borderColor: 'divider'
                      })}
                    >
                      <ClickAwayListener onClickAway={handleClose}>
                        <>
                          <SimpleBar sx={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: '50vh' }}>{navCollapse}</SimpleBar>
                        </>
                      </ClickAwayListener>
                    </Paper>
                  </Transitions>
                )}
              </PopperStyled>
            )}
          </ListItemButton>
          {drawerOpen && !menu?.isDropdown && (
            <Collapse in={open} timeout="auto" unmountOnExit>
              <List sx={{ p: 0 }}>{navCollapse}</List>
            </Collapse>
          )}

          {drawerOpen && menu?.isDropdown && (
            <Menu
              id={`${menu.id}-menu`}
              aria-labelledby={`${menu.id}-button`}
              anchorEl={anchorElCollapse}
              open={openCollapse}
              onClose={handleCloseCollapse}
              onClick={handleCloseCollapse}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={(theme) => ({ '& .MuiPaper-root': { boxShadow: theme.shadows[2] }, '& .MuiListItemButton-root': { pl: 2 } })}
            >
              {navCollapse}
            </Menu>
          )}
        </>
      ) : (
        <ListItemButton
          {...(menu?.url && { component: Link, to: menu.url })}
          id={`boundary-${popperId}`}
          disableRipple
          selected={isSelected}
          onMouseEnter={handleHover}
          onMouseLeave={handleClose}
          onClick={handleHover}
          aria-describedby={popperId}
          sx={(theme) => ({
            borderRadius: 1.5,
            color: isSelected || anchorEl ? activeColor : 'text.primary',
            backgroundColor: isSelected || anchorEl ? activeBg : 'transparent',
            '&:hover': {
              backgroundColor: isSelected || anchorEl ? activeBg : inactiveHoverBg,
              backgroundImage: isSelected || anchorEl ? activeGradient : hoverGradient
            },
            '&.Mui-selected': {
              bgcolor: `${activeBg} !important`,
              backgroundImage: `${activeGradient} !important`,
              boxShadow: activeShadow,
              color: `${activeColor} !important`
            }
          })}
        >
          <Box onClick={handlerIconLink} sx={FlexBox}>
            {menuIcon && <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 28, color: 'inherit' }}>{menuIcon}</ListItemIcon>}
            {!menuIcon && level !== 1 && (
              <ListItemIcon
                sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 28, bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' } }}
              >
                <Dot size={4} color={isSelected || anchorEl ? 'primary' : 'secondary'} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                <Typography variant="body1" color="inherit" sx={{ my: 'auto', fontWeight: isSelected || anchorEl ? 600 : 400 }}>
                  {translatedTitle}
                </Typography>
              }
            />
            {miniMenuOpened ? (
              <RightOutlined style={{ fontSize: '0.625rem', marginLeft: 8 }} />
            ) : (
              <DownOutlined style={{ fontSize: '0.625rem', marginLeft: 8 }} />
            )}
          </Box>

          {anchorEl && (
            <PopperStyled id={popperId} open={miniMenuOpened} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 2001 }}>
              {({ TransitionProps }) => (
                <Transitions in={miniMenuOpened} {...TransitionProps}>
                  <Paper sx={(theme) => ({ overflow: 'hidden', py: 0.5, boxShadow: theme.shadows[8], backgroundImage: 'none' })}>
                    <ClickAwayListener onClickAway={handleClose}>
                      <>
                        <SimpleBar sx={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: '50vh' }}>{navCollapse}</SimpleBar>
                      </>
                    </ClickAwayListener>
                  </Paper>
                </Transitions>
              )}
            </PopperStyled>
          )}
        </ListItemButton>
      )}
    </>
  );
}

NavCollapse.propTypes = {
  menu: PropTypes.any,
  level: PropTypes.number,
  parentId: PropTypes.string,
  setSelectedItems: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
  selectedItems: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  setSelectedLevel: PropTypes.func,
  selectedLevel: PropTypes.number
};
